import React from "react";
import { ListFieldDefinition, ObjectType } from "../../queries/getCrmListFieldDefinitions";
import { CellProps, Column } from "react-table";
import { ListViewColumn } from "../../queries/getCrmCustomListViews";
import { makeFullFormattedAddressWithObject } from "../../../utils/functions";
import { ContactAvatar } from "../../../features/permits/components/ContactAvatar";

interface IListViewDesktopColumns<T extends object, K extends Record<string, Omit<ListFieldDefinition, "fieldId">>> {
  columns: ListViewColumn[];
  defaultFelidDefinitions: Record<string, ListFieldDefinition>;
  customFieldDefinitions: Record<string, ListFieldDefinition>;
  overrideColumns: Record<keyof K, Column<T>>;
  mainRecords: T[];
}

export const ListViewDesktopColumns = <T extends object, K extends Record<string, ListFieldDefinition>>({
  columns,
  defaultFelidDefinitions,
  customFieldDefinitions,
  overrideColumns,
}: IListViewDesktopColumns<T, K>) => {
  const columnsWithDefinitions = columns
    .reduce(
      (acc, column) => {
        const fieldDefinition =
          defaultFelidDefinitions[column.fieldName] ||
          customFieldDefinitions[column.fieldName] ||
          Object.values(customFieldDefinitions).find((def) => def.fieldId === column.fieldId);
        if (!fieldDefinition) {
          return acc;
        }
        const lookupType = fieldDefinition.type === "LookUp" ? fieldDefinition.lookUpType : undefined;
        return [
          ...acc,
          {
            ...column,
            label: fieldDefinition.label,
            fieldId: fieldDefinition.fieldId,
            fieldType: fieldDefinition.type,
            lookupType,
          },
        ];
      },
      [] as (ListViewColumn & { label: string; lookupType: ObjectType | undefined })[],
    )
    .sort((a, b) => a.order - b.order);

  return columnsWithDefinitions.map((column) => {
    const overrideColumn = overrideColumns[column.fieldName as keyof K];
    if (overrideColumn) {
      return overrideColumn;
    }
    const isCustomField = column.fieldId !== undefined;
    const baseColumn: Column<T> = {
      Header: column.label,
      accessor: isCustomField ? (`customFields.${column.fieldName}.value` as keyof T) : (column.fieldName as keyof T),
    };

    const Cell: (data: React.PropsWithChildren<CellProps<T>>) => React.JSX.Element = (() => {
      switch (column.fieldType) {
        case "Text":
        //TODO: possibly add tel support for phone numbers
        case "PhoneNumber":
          return ({ cell }) => <span>{cell.value}</span>;
        case "TextArea":
          return ({ cell }) => {
            const value = cell.value as string;
            return <span>{value.length > 50 ? `${value.slice(0, 50)}...` : value}</span>;
          };
        case "Email":
          return ({ cell }) => {
            return <a href={`mailto:${cell.value}`}>{cell.value}</a>;
          };
        case "URL":
          return ({ cell }) => (
            <a href={cell.value} target='_blank' rel='noopener noreferrer'>
              {cell.value}
            </a>
          );
        case "Address":
          return ({ cell }) => <span>{makeFullFormattedAddressWithObject(cell.value)}</span>;
        case "Date":
          return ({ cell }) => {
            const date = new Date(cell.value);
            return (
              <span>
                {isNaN(date.getTime())
                  ? ""
                  : date.toLocaleDateString("en-us", {
                      month: "short",
                      year: "numeric",
                      day: "numeric",
                    })}
              </span>
            );
          };
        case "DateTime":
          return ({ cell }) => {
            const date = new Date(cell.value);
            return (
              <span>
                {isNaN(date.getTime())
                  ? ""
                  : date.toLocaleDateString("en-us", {
                      month: "short",
                      year: "numeric",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
              </span>
            );
          };
        case "Time":
          return ({ cell }) => {
            const date = new Date(cell.value);
            return (
              <span>
                {isNaN(date.getTime())
                  ? ""
                  : date.toLocaleDateString("en-us", {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
              </span>
            );
          };
        case "Number":
          return ({ cell }) => <span>{cell.value}</span>;
        case "Currency":
          return ({ cell }) => <span>${cell.value}</span>;
        case "Percent":
          return ({ cell }) => <span>{cell.value}%</span>;
        case "Checkbox":
          return ({ cell }) => {
            const value = cell.value as boolean | null;
            if (value === null) return <></>;
            const displayValue = value ? "Yes" : "No";
            return <span>{displayValue}</span>;
          };
        case "Dropdown":
          return ({ cell }) => {
            return <span>{cell.value}</span>;
          };
        case "DropdownMultiSelect":
          return ({ cell }) => <span>{cell.value.join(", ")}</span>;
        case "LookUp":
          return ({ cell }) => {
            if (column.lookupType === "Contact") {
              if (!cell.value?.value) return <span className='tw-italic'>Anonymous</span>;
              const contact = {
                userId: cell.value?.id,
                firstName: cell.value?.value?.split(" ")[0],
                lastName: cell.value?.value?.split(" ")[1],
                contactType: null,
              };
              return (
                <div className='tw-flex tw-items-center tw-gap-2'>
                  <ContactAvatar linkToConnectedCitizen={true} contact={contact} size='32px' />
                  {cell.value.value}
                </div>
              );
            }
            //TODO: add more look up type support
            return <span>{cell.value?.value}</span>;
          };
      }
    })();
    return {
      ...baseColumn,
      Cell,
    };
  });
};
